import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, Download, MoreVertical } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { pushModal, popModal, getTopModal, getNextModalZIndex, isBackNavigation, beginBackNavigation, endBackNavigation } from '../../utils/modalHistory';
import './ImageViewerModal.css';

const ImageViewerModal = ({ imageUrl, onClose, sourceRect }) => {
  const { theme } = useTheme();
  const { resolvedDirection } = useLanguage();
  const isRTL = resolvedDirection === 'rtl';
  const overlayRef = useRef(null), closeButtonRef = useRef(null), menuButtonRef = useRef(null), closeTimer = useRef(null);
  const onCloseRef = useRef(onClose), handleCloseRef = useRef(null);
  onCloseRef.current = onClose;
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [imageError, setImageError] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isZooming, setIsZooming] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const isClosingRef = useRef(false);
  const closedByPopstateRef = useRef(false);

  const instanceIdRef = useRef(null); // unique id for this instance

  const handleClose = () => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    setIsClosing(true);

    // If closed via UI (not back button), tell modalHistory to also pop browser history
    if (instanceIdRef.current) {
      popModal(instanceIdRef.current, !closedByPopstateRef.current);
    }

    closeTimer.current = setTimeout(() => onCloseRef.current(), reducedMotion ? 0 : 240);
  };

  const handleDownload = async () => {
    try {
      setShowMenu(false);
      menuButtonRef.current?.focus();
      
      // استخدام Cloudflare Worker proxy لتحميل الصورة (يتجاوز CORS)
      const proxyUrl = `https://download-image.amposter.workers.dev/?url=${encodeURIComponent(imageUrl)}`;
      
      const response = await fetch(proxyUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `amposter-image-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('فشل تحميل الصورة:', error);
      // فتح في تاب جديد كحل بديل
      window.open(imageUrl, '_blank');
    }
  };

  handleCloseRef.current = handleClose;
  // Keep one registration through StrictMode's setup/cleanup replay.
  const activeRef = useRef(false);
  useEffect(() => {
    activeRef.current = true;
    if (!instanceIdRef.current) instanceIdRef.current = pushModal('image-viewer');
    const previous = document.activeElement, previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    const handlePopState = event => {
      if (isBackNavigation() || event.state?.instanceId === instanceIdRef.current) return;
      if (getTopModal() === instanceIdRef.current && !isClosingRef.current) {
        beginBackNavigation();
        closedByPopstateRef.current = true;
        handleCloseRef.current();
        setTimeout(endBackNavigation, 0);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      activeRef.current = false;
      window.removeEventListener('popstate', handlePopState);
      clearTimeout(closeTimer.current);
      if (document.body.style.overflow === 'hidden') document.body.style.overflow = previousOverflow;
      if (previous?.isConnected) previous.focus();
      queueMicrotask(() => {
        if (!activeRef.current && !isClosingRef.current && instanceIdRef.current) {
          popModal(instanceIdRef.current, window.history.state?.instanceId === instanceIdRef.current);
        }
      });
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsZooming(false), 20);
    return () => clearTimeout(timer);
  }, []);

  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      event.preventDefault(); event.stopPropagation();
      if (showMenu) { setShowMenu(false); menuButtonRef.current?.focus(); } else handleClose();
    }
    if (event.key === 'Tab') {
      const controls = [...overlayRef.current.querySelectorAll('button:not(:disabled)')];
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  };

  // Calculate animation style based on source rect
  const getAnimationStyle = () => {
    if (!sourceRect) return {};

    const centerX = sourceRect.left + sourceRect.width / 2;
    const centerY = sourceRect.top + sourceRect.height / 2;
    const viewportCenterX = window.innerWidth / 2;
    const viewportCenterY = window.innerHeight / 2;

    return {
      '--start-x': `${centerX - viewportCenterX}px`,
      '--start-y': `${centerY - viewportCenterY}px`,
      '--start-scale': Math.min(sourceRect.width / window.innerWidth, sourceRect.height / window.innerHeight)
    };
  };

  return createPortal(
    <div ref={overlayRef} role="dialog" aria-modal="true" aria-label={isRTL ? 'عارض الصور' : 'Image viewer'} onKeyDown={handleKeyDown}
      className={`image-viewer-overlay ${isClosing ? 'closing' : ''} ${theme === 'dark' ? 'theme-dark-space' : 'theme-light-space'}`}
      dir={resolvedDirection}
      onClick={handleClose}
      style={{ zIndex: getNextModalZIndex(1000) }}
    >
      <div className="image-viewer-header">
        <button type="button" ref={closeButtonRef} aria-label={isRTL ? 'إغلاق الصورة' : 'Close image'} className="close-button" onClick={handleClose}>
          {isRTL ? <ArrowRight size={28} /> : <ArrowLeft size={28} />}
        </button>
        
        <div className="menu-container">
          <button type="button" ref={menuButtonRef} aria-label={isRTL ? 'خيارات الصورة' : 'Image options'} aria-expanded={showMenu} className="menu-button" onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}>
            <MoreVertical size={28} />
          </button>
          
          {showMenu && (
            <div className="menu-dropdown" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="menu-item" onClick={handleDownload}>
                <Download size={20} />
                <span>{isRTL ? 'تحميل الصورة' : 'Download image'}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div 
        className={`image-container ${isZooming ? 'zooming' : ''} ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        style={getAnimationStyle()}
      >
        {imageError ? <p className="image-viewer-error" role="alert">{isRTL ? 'تعذر تحميل الصورة' : 'This image could not be loaded'}</p> : <img src={imageUrl} alt={isRTL ? 'عرض الصورة' : 'Photo'} onError={() => setImageError(true)} />}
      </div>
    </div>,
    document.body
  );
};

export default ImageViewerModal;
